import React, { useEffect, useState } from 'react';
import { CreditCard, CheckCircle, AlertCircle, Loader2, Save, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { passAdmin } from '../../services/passApi';

export default function StripePassConfigCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [status, setStatus] = useState({
    configured: false,
    mode: 'test',
    secret_key_set: false,
    webhook_signing_secret_set: false,
  });

  const [form, setForm] = useState({
    mode: 'test',
    publishable_key: '',
    secret_key: '',
    webhook_signing_secret: '',
    currency: 'MXN',
  });

  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await passAdmin.getStripeConfig();
      setStatus(res.data);
      setForm((f) => ({
        ...f,
        mode: res.data.mode || 'test',
        publishable_key: res.data.publishable_key || '',
        currency: res.data.currency || 'MXN',
      }));
    } catch (err) {
      toast.error('Error cargando configuración Stripe');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.secret_key || !form.webhook_signing_secret || !form.publishable_key) {
      toast.error('Completa las 3 claves (publishable, secret, webhook)');
      return;
    }
    setSaving(true);
    try {
      await passAdmin.updateStripeConfig(form);
      toast.success('Configuración Stripe guardada');
      setForm((f) => ({ ...f, secret_key: '', webhook_signing_secret: '' }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await passAdmin.testStripeConfig();
      setTestResult(res.data);
      if (res.data.ok) {
        toast.success(`Conectado a Stripe (${res.data.mode})`);
      } else {
        toast.error(res.data.error || 'Sin conexión');
      }
    } catch (err) {
      toast.error('Error probando conexión');
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Pagos Pass — Stripe</h3>
            <p className="text-xs text-slate-500">
              Cobros in-app para reservas, depósitos, covers y compras completas.
            </p>
          </div>
        </div>
        {status.configured ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
            <CheckCircle className="w-3.5 h-3.5" /> Configurado
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
            <AlertCircle className="w-3.5 h-3.5" /> Sin configurar
          </span>
        )}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        {/* Mode */}
        <div className="flex gap-2">
          <ModeButton active={form.mode === 'test'} onClick={() => setForm((f) => ({ ...f, mode: 'test' }))}>
            Test (sandbox)
          </ModeButton>
          <ModeButton active={form.mode === 'live'} onClick={() => setForm((f) => ({ ...f, mode: 'live' }))} live>
            Live (producción)
          </ModeButton>
        </div>

        <Field label="Publishable Key (pk_...)" hint="Visible públicamente — no es secreta">
          <input
            type="text"
            value={form.publishable_key}
            onChange={(e) => setForm((f) => ({ ...f, publishable_key: e.target.value }))}
            placeholder="pk_test_..."
            className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm"
          />
        </Field>

        <Field
          label={
            <span className="flex items-center gap-2">
              Secret Key (sk_...)
              {status.secret_key_set && (
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">
                  <ShieldCheck className="w-3 h-3" /> guardada
                </span>
              )}
            </span>
          }
          hint="Solo se acepta nueva al guardar. No se muestra después."
        >
          <div className="relative">
            <input
              type={showSecret ? 'text' : 'password'}
              value={form.secret_key}
              onChange={(e) => setForm((f) => ({ ...f, secret_key: e.target.value }))}
              placeholder={status.secret_key_set ? '••••••••• (déjala vacía para no cambiar)' : 'sk_test_...'}
              className="w-full border border-slate-300 rounded-lg pl-3 pr-9 py-2 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowSecret((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>

        <Field
          label={
            <span className="flex items-center gap-2">
              Webhook signing secret (whsec_...)
              {status.webhook_signing_secret_set && (
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">
                  <ShieldCheck className="w-3 h-3" /> guardada
                </span>
              )}
            </span>
          }
          hint="Obtén el secret al crear el endpoint en Stripe Dashboard."
        >
          <div className="relative">
            <input
              type={showWebhook ? 'text' : 'password'}
              value={form.webhook_signing_secret}
              onChange={(e) => setForm((f) => ({ ...f, webhook_signing_secret: e.target.value }))}
              placeholder={status.webhook_signing_secret_set ? '••••••••• (déjala vacía para no cambiar)' : 'whsec_...'}
              className="w-full border border-slate-300 rounded-lg pl-3 pr-9 py-2 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowWebhook((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>

        <Field label="Moneda">
          <select
            value={form.currency}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="MXN">MXN — Peso mexicano</option>
            <option value="USD">USD — Dólar</option>
          </select>
        </Field>

        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
          <p className="font-semibold text-slate-700 mb-1">Webhook a configurar en Stripe:</p>
          <code className="block bg-white border border-slate-200 rounded px-2 py-1 font-mono">
            POST https://todoenlocal.com/api/v2/pass/payments/webhook
          </code>
          <p className="mt-2">
            Eventos: <span className="font-mono">checkout.session.completed</span>,{' '}
            <span className="font-mono">checkout.session.expired</span>.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar configuración
          </button>
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !status.configured}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Probar conexión
          </button>
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg text-sm ${testResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {testResult.ok
              ? `Conectado a Stripe. Cuenta ${testResult.account_id || ''} ${testResult.email ? `(${testResult.email})` : ''} — modo ${testResult.mode}`
              : `Error: ${testResult.error}`}
          </div>
        )}
      </form>
    </div>
  );
}

function ModeButton({ active, onClick, children, live }) {
  const base = 'flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors';
  if (active) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${base} ${live ? 'bg-red-600 border-red-600 text-white' : 'bg-slate-900 border-slate-900 text-white'}`}
      >
        {children}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${base} border-slate-300 text-slate-700 hover:bg-slate-50`}>
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
